import Dexie from 'dexie';

import { createExpense, uploadExpenseDocument } from '../api/expenses';

// crypto.randomUUID is the standard browser UUID v4 generator. Available in
// all modern phones we target (iOS Safari 15.4+, Android Chrome 92+); avoids
// pulling in the `uuid` npm package just for this.
const uuidv4 = () => crypto.randomUUID();

/**
 * Offline expense outbox — IndexedDB-backed.
 *
 * Records queued while offline are stored here as { payload, blob, ... }.
 * Each row carries its own client-generated UUID used as the
 * `Idempotency-Key` header on retry, so a flaky network can't double-create.
 *
 * Sharing the same Dexie database name as lib/trust-device-store.js is
 * intentional — Dexie merges versioned schemas across modules. Keep
 * `db.version()` calls consistent across files: each module that opens
 * `xenergy-pwa` MUST declare every table it cares about, even ones it
 * doesn't read, OR they'll be dropped on the next version bump.
 *
 * If both files race on first open, Dexie picks one schema; conflicts
 * surface as DexieError on store. Avoid by NOT bumping versions casually.
 */

let dbInstance = null;

function db() {
  if (!dbInstance) {
    dbInstance = new Dexie('xenergy-pwa');
    dbInstance.version(2).stores({
      // Existing from lib/trust-device-store.js (singleton)
      trustedDevice: '++id',
      // NEW in v2: outbox of expenses queued while offline
      expenseOutbox: '&clientId, status, createdAt',
    });
  }
  return dbInstance;
}

/**
 * Enqueue an expense + its receipt photo (Blob, optional) for sync.
 * Returns the clientId so the caller can show its status.
 */
export async function enqueueExpense({ payload, photoBlob, photoFilename }) {
  const clientId = uuidv4();
  const docClientId = photoBlob ? uuidv4() : null;
  await db().expenseOutbox.add({
    clientId,
    docClientId,
    payload,
    photoBlob: photoBlob || null,
    photoFilename: photoFilename || null,
    status: 'pending',
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
  });
  return clientId;
}

export async function listOutbox() {
  return db().expenseOutbox.orderBy('createdAt').reverse().toArray();
}

export async function pendingCount() {
  return db()
    .expenseOutbox.where('status')
    .anyOf('pending', 'failed')
    .count();
}

export async function deleteOutboxItem(clientId) {
  await db().expenseOutbox.delete(clientId);
}

/**
 * Drain the outbox. Best-effort: per-item failures are logged in the row
 * (status = 'failed', lastError set), but don't abort the loop.
 *
 * Idempotency-Key on the backend means a retry after a partial-network
 * failure replays the cached response without creating a duplicate.
 */
export async function syncOutbox() {
  const rows = await db()
    .expenseOutbox.where('status')
    .anyOf('pending', 'failed')
    .toArray();

  const results = { ok: 0, failed: 0 };

  for (const row of rows) {
    try {
      await db().expenseOutbox.update(row.clientId, {
        status: 'syncing',
        attempts: (row.attempts || 0) + 1,
      });

      const created = await createExpense(row.payload, {
        idempotencyKey: row.clientId,
      });

      if (row.photoBlob && row.docClientId) {
        await uploadExpenseDocument(
          created.id,
          row.photoBlob,
          row.photoFilename || 'receipt.jpg',
          { idempotencyKey: row.docClientId },
        );
      }

      // Success — remove from outbox.
      await db().expenseOutbox.delete(row.clientId);
      results.ok += 1;
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || 'Unknown sync error';
      await db().expenseOutbox.update(row.clientId, {
        status: 'failed',
        lastError: msg,
      });
      results.failed += 1;
    }
  }

  return results;
}
